import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import api from "../api/api";
import { useTypedSelector } from "../hooks/useTypedSelector";
import useUrlQuery from "../hooks/useUrlQuery";
import { MembersInterface } from "../modules/landing.slice";
import { setActiveProposal, setProposalData } from "../modules/proposal.slice";
import { setSelectedRiders, setSelectedTenure } from "../modules/quotes.slice";
import { SelectOptions } from "../pages/ProposalPage/components/InputComponents/Select";
import { RiderInterface } from "./Quotes.service";
import { useEffect } from "react";
interface InitiateProposalRequest {
  PRODUCT_ID?: number;
  COMPANY_SLUG?: string;
  QUOTE_ID?: string;
  TOTAL_PREMIUM?: string;
  SUM_INSURED?: string;
  ADD_ONS?: RiderInterface[];
  TENURE?: string;
  NET_PREMIUM?: string;
  PORT_POLICY?: boolean;
  selected_members?: any;
  BLOCK_PORTING?: any;
  port_flag?: boolean;
}
interface InitiateProposalResponse {
  status: 200 | 500;
  response: {
    PROPOSAL_ID: string;
    session_token: string;
  };
}
export const useInitiateProposalMuation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { mutate, isPending } = useMutation({
    mutationFn: (req: InitiateProposalRequest) =>
      api.post<InitiateProposalResponse>("api/InitiateHealthProposal", req),

    onSuccess: (res) => {
      const token = res?.data?.response?.session_token;

      if (token) {
        sessionStorage.setItem("session_token", token);
      }

      dispatch(setSelectedRiders([]));

      navigate({
        pathname: "/product-summary",
        search: `?proposal_id=${res.data.response.PROPOSAL_ID}&session_token=${token}`,
      });
    },
  });

  return {
    initiateProposal: mutate,
    isProposalInitiating: isPending,
  };
};

interface ProposalDetailsResponse {
  status: 200 | 500;
  data: {
    PROPOSAL: {
      _id: string;
      QUOTE_ID: string;
      PRODUCT_ID: number;
      COMPANY_SLUG: string;
      TOTAL_PREMIUM: string;
      SUM_INSURED: string;
      updated_at: string;
      created_at: string;
      TENURE: string;
      ADD_ONS: RiderInterface[];
      FORMDATA: any;
      PORT_POLICY?: boolean;
      NET_PREMIUM: string;
      BLOCK_PORTING?: boolean;
      ISPORTING?: boolean;
      port_flag?: boolean;
      selected_members?: any;
    };
    QUOTE_REQUEST: {
      _id: string;
      pincode: string;
      mobile_no: string;
      members: MembersInterface[];
      policy_type: "I" | "F";
      requested_si: string;
      updated_at: string;
      created_at: string;
      proposer_gender: string;
      BLOCK_PORTING?: boolean;
    };
    product_data: {
      PRODUCTID: number;
      IC_ID: string;
      ADULT_MIN: string;
      ADULT_MAX: string;
      LOB: string;
      POLICY_TYPE: string;
      PRODUCT_TYPE: string;
      PRODUCT_NAME: string;
      PRODUCT_IDENTIFIER: string;
      CREATED_DATE: string;
      UPDATED_DATE: string;
      INSURANCE_NAME: string;
      INSURANCE_SLUG: string;
      ADDRESS: string;
      CONTACT_DETAILS: string;
      BROCHURE_LINK: string;
      LOGO_PATH: string;
      CHILD_MIN: number;
      CHILD_MAX: number;
    };
  };
}
export const useGetProposalDetailsQuery = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const dispatch = useDispatch();

  const query = useQuery({
    queryKey: ["proposal", proposal_id],
    queryFn: async () => {
      const res = await api.post("/api/GetHealthProposalDetails", {
        PROPOSAL_ID: proposal_id,
      });
      return res.data;
    },

    enabled: !!proposal_id,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const data = query.data;

  useEffect(() => {
    if (!data) return;

    if (data?.data?.PROPOSAL?.ADD_ONS) {
      dispatch(setSelectedRiders(data?.data?.PROPOSAL.ADD_ONS));
      // dispatch(setSelectedTenure(data.data.PROPOSAL.TENURE));
    }

    if (data?.data?.PROPOSAL?.FORMDATA) {
      dispatch(setProposalData(data.data.PROPOSAL.FORMDATA));
    }
  }, [data, dispatch]);

  return {
    members: data?.data?.QUOTE_REQUEST?.members,
    enquiry: data?.data?.QUOTE_REQUEST,
    quote_data: data?.data?.PROPOSAL,
    product: data?.data?.product_data,
    isCKYC: ["star", "manipal_cigna", "care"].includes(
      data?.data?.product_data?.INSURANCE_SLUG || ""
    ),
    isPortability: data?.data?.PROPOSAL?.PORT_POLICY,
    isProposalLoading: query.isPending || query.isFetching,
  };
};

export interface ProposalQuestions {
  ID: number;
  QUESTION_TITLE: string;
  QUESTION_ALIAS: string;
  IC_ID: 11;
  ANSWER_TYPE:
  | "BOOLEAN"
  | "DROPDOWN"
  | "FREE_TEXT"
  | "TOGGLE"
  | "DATE"
  | "MONTH_YEAR";
  PARENT_ID: string | null;
  PARENT_KEY: string | null;
  VALUE: SelectOptions[];
  RESTRICT_KEY: string | null;
  RESTRICT_OPERATOR: string | null;
  HAS_CHILD: "Y" | "N";
  CREATED_AT: string;
  UPDATED_AT: string;
  ADDITIONAL?: ProposalQuestions[];
  VIEWS?: Array<"year" | "month" | "day">;
  TEXTMASK?: string;
  MAXLENGTH?: number;
  ALLOW_ONLY_FUTURE?: "0" | "1";
  allow_multiple?: boolean;
  LIFESTYLE_QUESTION?: ProposalQuestions[];
}
interface ProposalQuestionsResponse {
  data: ProposalQuestions[];
  status: 200 | 500;
}
export const useGetProposalQuestions = () => {
  const { product } = useGetProposalDetailsQuery();
  const { getUrlQuery } = useUrlQuery();

  const PROPOSAL_ID = getUrlQuery("proposal_id");

  const { data } = useQuery({
    queryKey: ["question", product?.IC_ID, product?.PRODUCTID, PROPOSAL_ID],
    queryFn: async () => {
      const res = await api.post<ProposalQuestionsResponse>(
        "/api/GetHealthQADetails",
        {
          IC_ID: product?.IC_ID,
          PRODUCT_ID: product?.PRODUCTID,
          proposal_id: PROPOSAL_ID,
        }
      );

      return res.data;
    },
    enabled: !!product?.IC_ID && !!product?.PRODUCTID && !!PROPOSAL_ID,
  });

  return {
    questions: data?.data,
  };
};
export const useGetLifeStyleQuestions = () => {
  const { product } = useGetProposalDetailsQuery();
  const { getUrlQuery } = useUrlQuery();

  const PROPOSAL_ID = getUrlQuery("proposal_id");

  const { data } = useQuery({
    queryKey: [
      "lifestyle_questions",
      product?.IC_ID,
      product?.PRODUCTID,
      PROPOSAL_ID,
    ],
    queryFn: async () => {
      const res = await api.post<ProposalQuestionsResponse>(
        "/api/GetHealthLifeStyleQuestions",
        {
          IC_ID: product?.IC_ID,
          PRODUCT_ID: product?.PRODUCTID,
          proposal_id: PROPOSAL_ID,
        }
      );

      return res.data;
    },
    enabled: !!product?.IC_ID && !!product?.PRODUCTID && !!PROPOSAL_ID,
  });

  return {
    lifestyleQuestions: data?.data,
  };
};

export const useUpdateProposalMutation = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const token = getUrlQuery("session_token");


  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: async (req: InitiateProposalRequest) => {
      const res = await api.post("api/UpdateHealthProposal", {
        ...req,
        PROPOSAL_ID: proposal_id,
      });
      return res.data;
    },

    onSuccess: () => {
      navigate({
        pathname: "/proposal",
        search: `?proposal_id=${proposal_id}&session_token=${token}`,
      });

      queryClient.removeQueries({
        queryKey: ["proposal"],
      });
    },
  });

  return {
    updateProposal: mutate,
  };
};

export const useUpdateProposalFormData = (onSuccess = () => { }) => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { proposalData, activeProposal } = useTypedSelector(
    (state) => state.proposal
  );

  const { mutate, mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: async (req: any) => {
      const response = await api.post("api/UpdateRawHealthProposal", {
        PROPOSAL_ID: proposal_id,
        FORMDATA: {
          ...proposalData,
          ...req,
        },
      });

      if (response?.status === 500 && response?.data?.showpopup) {
        Swal.fire("Alert", response?.data?.message);
      }

      return response.data;
    },

    onSuccess: async () => {
      await api.post("api/RecalculateHealthPremium", {
        proposal_id,
      });

      queryClient.removeQueries({
        queryKey: ["proposal"],
      });

      dispatch(setActiveProposal(activeProposal + 1));

      if (onSuccess) {
        onSuccess();
      }
    },

    onError: (err: any) => {
      if (err?.response?.data?.status === 500) {
        if (err?.response?.data?.showpopup) {
          Swal.fire("Alert", err?.response?.data?.message);
        } else {
          toast.error(
            err?.response?.data?.message ||
            "Something went wrong while submitting the proposal"
          );
        }
      }
    },
  });

  return {
    saveForm: mutate,
    asyncSaveForm: mutateAsync,
    isSaving: isPending,
    isSaved: isSuccess,
  };
};
interface PincodeRequest {
  pincode: string;
  company_alias?: string;
}
interface PincodeResponse {
  status: 200 | 500;
  response: {
    city: SelectOptions[];
    state: [SelectOptions];
  };
}
interface MasterResponse {
  status: 200 | 500;
  response: SelectOptions[];
}
export const useGetPincodeMutation = () => {
  const { mutate, data, isPending, isError, isSuccess } = useMutation({
    mutationFn: async ({ pincode, company_alias }: PincodeRequest) => {
      const res = await api.post<PincodeResponse>("api/GetPincodeMaster", {
        pincode,
        company_alias,
      });
      return res.data;
    },

    onSettled: (data) => {
      if (data?.status === 500) {
        toast.error("Pincode not found");
      }
    },
  });

  return {
    isCityStateLoading: isPending,
    getCityState: mutate,
    cityOptions: data?.response?.city,
    stateOptions: data?.response?.state,
    isError,
    isCityStateFetched: isSuccess,
  };
};

export const useGetRelationMasterQuery = () => {
  const { quote_data } = useGetProposalDetailsQuery();

  const { data, isPending } = useQuery({
    queryKey: [
      "relation",
      quote_data?.COMPANY_SLUG,
      quote_data?.PRODUCT_ID,
    ],

    queryFn: async () => {
      const res = await api.post<MasterResponse>("api/GetRelationMaster", {
        LOB: "HEALTH",
        COMPANY_SLUG: quote_data?.COMPANY_SLUG,
        PRODUCT_ID: quote_data?.PRODUCT_ID,
        type: "nominee",
      });

      return res.data;
    },

    enabled: !!quote_data?.COMPANY_SLUG && !!quote_data?.PRODUCT_ID,
  });

  return {
    relations: data?.response,
    isRelationsLoading: isPending,
  };
};

export const useGetRelationMasterAppointeeQuery = (
  type: "appointee",
  enabled: boolean
) => {
  const { quote_data } = useGetProposalDetailsQuery();

  const { data, isPending } = useQuery({
    queryKey: [
      "relation",
      quote_data?.COMPANY_SLUG,
      quote_data?.PRODUCT_ID,
      type,
    ],

    queryFn: async () => {
      const res = await api.post<MasterResponse>("api/GetRelationMaster", {
        LOB: "HEALTH",
        COMPANY_SLUG: quote_data?.COMPANY_SLUG,
        PRODUCT_ID: quote_data?.PRODUCT_ID,
        type,
      });

      return res.data;
    },

    enabled:
      enabled &&
      !!quote_data?.COMPANY_SLUG &&
      !!quote_data?.PRODUCT_ID,

    placeholderData: (prev) => prev, // replacement for keepPreviousData
  });

  return {
    appointeeRelations: data?.response,
    isRelationsLoading: isPending,
  };
};

export const useGetOccupationQuery = () => {
  const { quote_data } = useGetProposalDetailsQuery();

  const { data, isPending } = useQuery({
    queryKey: ["occupation", quote_data?.COMPANY_SLUG],

    queryFn: async () => {
      const res = await api.post<MasterResponse>("api/GetOccupationMaster", {
        COMPANY_SLUG: quote_data?.COMPANY_SLUG,
        LOB: "HEALTH",
      });

      return res.data;
    },

    enabled: !!quote_data?.COMPANY_SLUG,
  });

  return {
    isOccupationLoading: isPending,
    occupation_list: data?.response,
  };
};

export const serialize = function (obj: any) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
};
const loadRazorpayScript = () => {
  return new Promise<boolean>((resolve) => {
    if ((window as any).Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;

    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

export const handlePGForm = async (PG_FORM: any) => {
  if (!PG_FORM) return;
  const { fields, url } = PG_FORM;
  const method = PG_FORM?.method?.toUpperCase();

  if (method === "RAZORPAY") {
    try {
      const loaded = await loadRazorpayScript();

      if (!loaded) {
        toast.error("Failed to load Razorpay SDK");
        return;
      }

      if (typeof url === "object") {
        const rzp = new (window as any).Razorpay(url);

        rzp.on("payment.failed", (response: { error: any }) => {
          console.error("Razorpay failed:", response.error);
          toast.error("Payment failed. Please try again.");
        });

        rzp.open();
      } else {
        toast.error("Invalid Razorpay configuration.");
      }
    } catch (error) {
      console.error("Razorpay init error:", error);
      toast.error("Error initiating payment.");
    }
    return;
  }

  if (method === "POST") {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;

    Object.keys(fields || {}).forEach((key) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = fields[key];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    return;
  }

  if (method === "GET") {
    const hasFields = fields && Object.keys(fields).length > 0;

    if (hasFields) {
      const query = new URLSearchParams(fields).toString();
      window.location.href = `${url}?${query}`;
    } else {
      window.location.href = url;
    }
    return;
  }

  console.warn("Unknown payment method, fallback redirect");
  window.open(url, "_self");
};

export const useProposalFinalSubmitMutation = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const { quote_data } = useGetProposalDetailsQuery();
  const { initiatePayment } = usePaymentQuery();
  const { refetch: fetchHdfcDocument } = useGetHdfcDocument();

  const { mutate, data, isPending } = useMutation({
    mutationFn: async () => {
      const res = await api.post("api/HealthProposalFinalSubmit", {
        PROPOSAL_ID: proposal_id,
      });
      return res.data;
    },

    onSuccess: (res) => {

      if (!res) {
        console.error("No valid response");
        return;
      }

      if (Number(res?.status) === 500) {
        toast.error(res?.message || "Server Error");
        return;
      }

      if (res?.showpopup) {
        Swal.fire("Alert", res?.message);
      }

      if (Number(res?.status) === 200) {
        const handlePostSubmit = async () => {
          if (
            quote_data?.COMPANY_SLUG !== "hdfc_ergo_optimasecure" &&
            res?.uw_data?.uw_flag
          ) {
            await Swal.fire(
              "NSTP",
              "This proposal will be sent for doctor approval. The policy will be converted only after approval."
            );
          }

          const pgForm = res?.PG_FORM || res?.return_data;
          if (pgForm) {
            await handlePGForm(pgForm);
          } else if (quote_data?.COMPANY_SLUG === "hdfc_ergo_optimasecure") {
            fetchHdfcDocument();
          } else {
            initiatePayment();
          }
        };
        handlePostSubmit();
      }
    },

    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
        "Something went wrong while submitting the proposal"
      );
    },
  });

  return {
    isSubmitting: isPending,
    submitProposal: mutate,
    data,
    success: data?.status,
  };
};
interface IOptions {
  onCKYCIncomplete?: () => void;
}

// export const usePaymentQuery = (options?: IOptions) => {
//   const { getUrlQuery } = useUrlQuery();
//   const proposal_id = getUrlQuery("proposal_id");
//   const navigate = useNavigate();

//   const { mutate, data, isPending } = useMutation({
//     mutationFn: async () => {
//       const res = await api.post("api/GetHealthPaymentDetails", {
//         PROPOSAL_ID: proposal_id,
//       });
//       return res.data;
//     },

//     onSuccess: async (res) => {
//       if (res?.status === 500) {
//         toast.error(res?.message);
//         return;
//       }

//       if (res?.status === 200) {
//         const { PG_FORM, CKYC_LINK } = res;

//         // CKYC check
//         if (CKYC_LINK) {
//           window.open(CKYC_LINK, "_blank");
//           options?.onCKYCIncomplete?.();
//           return;
//         }

//         if (!PG_FORM) {
//           console.error("PG_FORM missing");
//           return;
//         }

//         const { fields, method, url } = PG_FORM;

//         // Razorpay Payment
//         if (method === "RAZORPAY") {
//           try {
//             const loaded = await loadRazorpayScript();

//             if (!loaded) {
//               toast.error("Failed to load Razorpay SDK");
//               return;
//             }

//             if (typeof url === "object") {
//               const rzp = new (window as any).Razorpay(url);

//               rzp.on("payment.failed", (response: { error: any }) => {
//                 console.error("Razorpay failed:", response.error);
//                 toast.error("Payment failed. Please try again.");
//               });

//               rzp.open();
//             } else {
//               toast.error("Invalid Razorpay configuration.");
//             }
//           } catch (error) {
//             console.error("Razorpay init error:", error);
//             toast.error("Error initiating payment.");
//           }

//           return;
//         }

//         // POST Payment Gateway
//         if (method === "POST") {
//           const form = document.createElement("form");
//           form.method = "POST";
//           form.action = url;

//           Object.keys(fields || {}).forEach((key) => {
//             const input = document.createElement("input");
//             input.type = "hidden";
//             input.name = key;
//             input.value = fields[key];
//             form.appendChild(input);
//           });

//           document.body.appendChild(form);
//           form.submit();
//           document.body.removeChild(form);
//           return;
//         }

//         // GET / Redirect payment
//         if (fields) {
//           const query = new URLSearchParams(fields).toString();
//           window.open(query ? `${url}?${query}` : url, "_self");
//         } else {
//           window.open(url, "_self");
//         }
//       }
//     },

//     onError: (error: any) => {
//       console.error("Payment API error:", error);
//       toast.error(
//         error?.response?.data?.message ||
//         "Something went wrong while fetching payment details."
//       );
//     },
//   });

//   return {
//     paymentData: data,
//     isPaymentLoading: isPending,
//     initiatePayment: mutate,
//   };
// };

export const usePaymentQuery = (options?: IOptions) => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const navigate = useNavigate();

  const { mutate, data, isPending } = useMutation({
    mutationFn: async () => {
      const res = await api.post("api/GetHealthPaymentDetails", {
        PROPOSAL_ID: proposal_id,
      });
      return res.data;
    },

    onSuccess: async (res) => {
      if (res?.status === 500) {
        toast.error(res?.message || "Something went wrong");
        return;
      }

      if (res?.status === 200) {
        const { CKYC_LINK } = res;

        // ✅ Handle both response formats
        const PG_FORM = res?.PG_FORM || res?.return_data;

        // 🔐 CKYC Handling
        if (CKYC_LINK) {
          window.open(CKYC_LINK, "_blank");
          options?.onCKYCIncomplete?.();
          return;
        }

        // ❌ If still missing
        if (!PG_FORM) {
          console.error("PG_FORM missing");
          return;
        }

        await handlePGForm(PG_FORM);
      }
    },

    onError: (error: any) => {
      console.error("Payment API error:", error);
      toast.error(
        error?.response?.data?.message ||
        "Something went wrong while fetching payment details."
      );
    },
  });

  return {
    paymentData: data,
    isPaymentLoading: isPending,
    initiatePayment: mutate,
  };
};
export const useGetCouncilMasterQuery = () => {
  const { quote_data } = useGetProposalDetailsQuery();

  const { data, isPending } = useQuery({
    queryKey: ["medical_council", quote_data?.COMPANY_SLUG],

    queryFn: async () => {
      const res = await api.post<any>("api/GetMedicalCouncilMaster", {
        LOB: "HEALTH",
        COMPANY_SLUG: quote_data?.COMPANY_SLUG,
      });

      return res.data;
    },

    enabled: !!quote_data?.COMPANY_SLUG,
  });

  return {
    council_names: data?.response?.map((item: any) => ({
      OPTION_KEY: item?.COUNCIL_NAME,
      OPTION_VAL: item?.COUNCIL_NAME,
    })),
    isCouncilNamesLoading: isPending,
  };
};

interface BankDetails {
  bankAccountNo: any;
  BANK: string;
  BRANCH: string;
  CITY: string;
  STATE: string;
  IFSC: string;
  bankAccountType: any;
  micrCode: string;
  bankName: string;
}

const fetchBankDetailsByIFSC = async (
  ifscCode: string,
  proposal_id: string
): Promise<BankDetails | null> => {
  if (!ifscCode) return null;
  try {
    const response = await api.get("/api/GetBankDetailsByIFSC", {
      params: {
        ifscCode,
        PROPOSAL_ID: proposal_id,
      },
    });
    return response?.data?.response;
  } catch (error) {
    console.error("Error fetching bank details:", error);
    toast.error(
      "Failed to fetch bank details");
    throw new Error("Failed to fetch bank details");
  }
};

export const useGetBankDetailsByIFSC = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const mutation = useMutation({
    mutationFn: async (ifscCode: string) => {
      return fetchBankDetailsByIFSC(ifscCode, proposal_id);
    },

    onSuccess: () => {
      // optional success handling
    },

    onError: (error) => {
      console.error("Error fetching bank details:", error);
    },
  });

  return mutation;
};

export const useUploadPortingFile = () => {
  const upload = async (formData: any) => {
    try {
      const response = await api.post("/api/uploadFile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  return { upload };
};

export const useGetHdfcDocument = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const fetchHdfcDocument = async () => {
    const response = await api.get("/api/HealthHDFCCISDocument", {
      params: { proposal_id },
    });
    return response.data;
  };

  const { data, isError, error, refetch, isPending } = useQuery({
    queryKey: ["hdfcDocument", proposal_id],

    queryFn: fetchHdfcDocument,

    enabled: false, // manual fetch only
  });

  // Handle success/error after fetching
  useEffect(() => {
    if (data?.status === 500) {
      toast.error(data?.message);
    }
  }, [data]);

  return {
    message: data?.message,
    error,
    refetch,
    isLoading: isPending,
    hdfcDocument: data,
    isError,
  };
};

export const useVerifyDocumentOtp = () => {
  const { getUrlQuery } = useUrlQuery();
  const { initiatePayment } = usePaymentQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const verifyDocumentOTP = async (otp: string) => {
    const response = await api.post("/api/VerifyDocumentOTP", {
      proposal_id, // Pass the proposal_id and otp in the payload
      otp,
    });
    return response; // Return the response for further handling
  };

  const handleVerifyOtp = async (otp: string) => {
    try {
      const otpResponse = await verifyDocumentOTP(otp);
      if (otpResponse?.status === 200) {
        initiatePayment();
        return otpResponse;
      }

      // Check if the response contains status 400 for invalid OTP
      if (otpResponse?.data?.status === 400) {
        return otpResponse; // Return the response with status 400 for further handling
      }
    } catch (error) {
      console.error("OTP verification failed", error);
      throw new Error(
        "An error occurred while verifying OTP. Please try again."
      );
    }
  };

  return {
    handleVerifyOtp, // Return the updated function
  };
};

export const useGetHdfcBankNames = (query: string) => {
  const fetchBankNames = async () => {
    const response = await api.get("api/GetHDFCBankNames", {
      params: { bank_name: query },
    });
    return response.data;
  };

  const { data, isError, error, refetch, isPending } = useQuery({
    queryKey: ["hdfcBankNames", query],

    queryFn: fetchBankNames,

    enabled: !!query && query.length >= 1,

  });

  useEffect(() => {
    if (data?.status === 500) {
      toast.error(data?.message);
    }
  }, [data]);

  useEffect(() => {
    if (isError && error) {
      toast.error(
        (error as any)?.response?.data?.message ||
        "Error while fetching bank names"
      );
    }
  }, [isError, error]);

  return {
    bankNames: data?.response || [],
    errors: error,
    refetch,
    isLoadings: isPending,
    isError,
  };
};
export const useIndustryType = (companySlug?: string) => {
  const isEnabled = companySlug === "hdfc_ergo_optimasecure";

  const { data, isPending } = useQuery({
    queryKey: ["industry_type", companySlug],

    queryFn: async () => {
      const res = await api.post<MasterResponse>("api/GetIndustryMaster", {
        COMPANY_SLUG: companySlug,
        LOB: "HEALTH",
      });

      return res.data;
    },

    enabled: isEnabled,
  });

  return {
    isLoading: isPending,
    industry_type_list: data?.response,
  };
};

//   const {quote_data} = useGetProposalDetailsQuery();
// const { isLoading, data } = useQuery(
//     ["industry_type", quote_data?.COMPANY_SLUG],
//   ()=>{
//     return api.post<MasterResponse>("api/GetIndustryMaster", {
//       COMPANY_SLUG: quote_data?.COMPANY_SLUG,
//       LOB: "HEALTH",
//     });
//   }
// );
//   return {
//     isLoading,
//     industry_type_list : data?.data.response,
//   };
// };


export const getMobileNumber = async (PROPOSAL_ID: string) => {
  const res = await api.post("/api/getProposalOTP", {
    PROPOSAL_ID,
  });

  return res.data;
};

export const verifyOtpApi = async (
  PROPOSAL_ID: string,
  OTP: string
) => {
  const res = await api.post("/api/verifyProposalOTP", {
    PROPOSAL_ID,
    OTP,
  });

  return res.data;
};